const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/modules/CIS4m/components/CIS4mSingleCheckbox.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4mValidation } from '../hooks';
import { CIS4mValueComponentProps } from '../types';
import { Checkbox, FormControl, FormControlLabel, FormHelperText } from '@mui/material';
import { booleanToString, stringToBoolean } from '../utils/data';
import { useTheme } from '@mui/material/styles';
import { TextInput, useText } from '../../../utils/intlUtils';

interface Props extends CIS4mValueComponentProps {
    label: TextInput;
    alternativeTrueValue?: string; // instead of '1' as default value for true
    required?: boolean;
    errorMessageWhenRequired?: TextInput; // only usable when required = true
    initialValue?: boolean;
    // convert the data stored to express whether the checkbox is checked; nice when the stored data is something that's not the default implementation
    parseValue?: (value: string | undefined) => boolean;
    // determine the value used to store in the data to express whether the checkbox is checked
    serializeValue?: (checked: boolean) => string | undefined;
}

export const CIS4mSingleCheckbox = forwardRef((props: Props, ref: any) => {
    const theme = useTheme();
    const { path, initialValue, disabled = false, alternativeTrueValue, errorMessageWhenRequired } = props;

    const label = useText(props.label);

    const hidden = props.hidden ?? false;
    const required = props.required ?? false;

    const parseValue = props.parseValue ?? (value => stringToBoolean(value));

    const serializeValue = props.serializeValue ?? (checked => booleanToString(checked, alternativeTrueValue));

    const [value, setValue, errorMessage] = useCIS4mValidation(ref, {
        disabled,
        errorMessageWhenRequired,
        hidden,
        initialValue: booleanToString(initialValue, alternativeTrueValue),
        path,
        required,
    });

    const labelElement = (
        <span style={{ color: errorMessage ? theme.palette.error.main : 'rgba(0, 0, 0, 0.6)' }}>
            {label}
            {required ? (
                <span aria-hidden={true} style={{ margin: '-2px 0 0 2px' }}>
                    *
                </span>
            ) : null}
        </span>
    );

    return hidden ? null : (
        <div>
            <FormControl required={required} error={!!errorMessage} disabled={disabled}>
                <FormControlLabel
                    labelPlacement={'end'}
                    label={labelElement}
                    control={
                        <Checkbox
                            checked={parseValue(value)}
                            size={'small'}
                            onChange={(event: any) => setValue(serializeValue(event.target.checked))}
                            style={{ color: errorMessage ? theme.palette.error.main : '' }}
                        />
                    }
                    disabled={disabled}
                />
                {!!errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
            </FormControl>
        </div>
    );
});

CIS4mSingleCheckbox.displayName = 'CIS4mSingleCheckbox';

export default CIS4mSingleCheckbox;
